import api from './client';
import { getApiHostUrl } from '../config/api';

// Interfaces
export interface PhotoMetadata {
  width?: number;
  height?: number;
  size?: number;
  format?: string;
  originalName?: string;
  mimeType?: string;
}

export interface Photo {
  id: string;
  url: string;
  caption?: string;
  birdId?: string;
  speciesId?: string;
  isAvatar: boolean;
  tags?: string[];
  metadata?: PhotoMetadata;
  filename?: string;
  path?: string;
  bird?: any;
  species?: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePhotoDto {
  url?: string;
  caption?: string;
  birdId?: string;
  speciesId?: string;
  tags?: string[];
  metadata?: PhotoMetadata;
  isAvatar?: boolean;
  filename?: string;
  path?: string;
}

export interface UpdatePhotoDto extends Partial<CreatePhotoDto> {}

export interface GetPhotosDto {
  page?: number;
  limit?: number;
  search?: string;
  photoType?: 'bird' | 'species' | 'general';
  birdId?: string;
  speciesId?: string;
  isAvatar?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  tags?: string[];
  startDate?: string;
  endDate?: string;
}

export interface PhotosResponse {
  success: boolean;
  data: Photo[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PhotoStats {
  total: number;
  byType: Record<string, number>;
  recentUploads: number;
  totalFileSize: number;
  avatarCount: number;
}

// API functions
export const photosApi = {
  async getPhotos(filters: GetPhotosDto = {}): Promise<Photo[]> {
    const result = await this.getPhotosPaginated(filters);
    return result.data;
  },

  async getPhotosPaginated(filters: GetPhotosDto = {}): Promise<PhotosResponse> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          params.set(key, value.toString());
        }
      }
    });

    const response = await api.get<{ success: boolean; data: Photo[]; total: number; page: number; limit: number; totalPages: number }>(`photos?${params.toString()}`);
    return {
      success: response.data.success,
      data: response.data.data,
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      totalPages: response.data.totalPages,
    };
  },

  async getPhoto(id: string): Promise<Photo> {
    const response = await api.get<{ success: boolean; data: Photo }>(`photos/${id}`);
    return response.data.data;
  },

  async createPhoto(data: CreatePhotoDto): Promise<Photo> {
    const response = await api.post<{ success: boolean; data: Photo }>('photos', data);
    return response.data.data;
  },

  async updatePhoto(id: string, data: UpdatePhotoDto): Promise<Photo> {
    const response = await api.patch<{ success: boolean; data: Photo }>(`photos/${id}`, data);
    return response.data.data;
  },

  /** Replace the photo image file (e.g. after editing). FormData must include key "file" with the image file. */
  async replacePhotoImage(id: string, formData: FormData): Promise<Photo> {
    const response = await api.patch<{ success: boolean; data: Photo }>(`photos/${id}/upload`, formData);
    return response.data.data;
  },

  async deletePhoto(id: string): Promise<void> {
    await api.delete(`photos/${id}`);
    return;
  },

  async getStatistics(): Promise<PhotoStats> {
    const response = await api.get<{ success: boolean; data: PhotoStats }>('photos/statistics');
    return response.data.data;
  },

  async getPhotosByBird(birdId: string): Promise<Photo[]> {
    const response = await api.get<{ success: boolean; data: Photo[] }>(`photos/by-bird/${birdId}`);
    return response.data.data;
  },

  async getPhotosBySpecies(speciesId: string): Promise<Photo[]> {
    const response = await api.get<{ success: boolean; data: Photo[] }>(`photos/by-species/${speciesId}`);
    return response.data.data;
  },

  async setAsAvatar(photoId: string, birdId: string): Promise<Photo> {
    const response = await api.post<{ success: boolean; data: Photo }>(`photos/${photoId}/avatar`);
    return response.data.data;
  },

  async uploadPhoto(formData: FormData): Promise<Photo> {
    const response = await api.post<{ success: boolean; data: Photo }>('photos', formData);
    return response.data.data;
  },

  async bulkUpload(formData: FormData): Promise<Photo> {
    const response = await api.post<{ success: boolean; data: Photo }>('photos/bulk-upload', formData);
    return response.data.data;
  },

  async uploadBirdPhoto(formData: FormData): Promise<Photo> {
    const response = await api.post<{ success: boolean; data: Photo }>(`photos/bird/${formData.get('birdId')}`, formData);
    return response.data.data;
  },

  async uploadSpeciesPhoto(formData: FormData): Promise<Photo> {
    const speciesId = formData.get('speciesId');
    formData.delete('speciesId'); // Remove speciesId from FormData since it will be in URL
    // Backend returns Photo directly (not wrapped in success/data)
    const response = await api.post<Photo>(`photos/species/${speciesId}`, formData);
    return response.data;
  }
};

// Helper functions
export const getPhotoUrl = (photoIdOrUrl: string): string => {
  if (!photoIdOrUrl) {
    return '';
  }

  const baseUrl = getApiHostUrl();
  const picGalleryName = photoIdOrUrl.match(/\/PicGallery\/([^/?#]+)/)?.[1];
  if (picGalleryName) {
    return `${baseUrl}/api/photos/file/${encodeURIComponent(picGalleryName)}`;
  }

  // If it's already a full URL (non-gallery), return as-is
  if (photoIdOrUrl.startsWith('http://') || photoIdOrUrl.startsWith('https://')) {
    return photoIdOrUrl;
  }
  
  // If it's a path starting with /api/, construct full URL
  if (photoIdOrUrl.startsWith('/api/')) {
    return `${baseUrl}${photoIdOrUrl}`;
  }
  
  // Check if it looks like a UUID (photo ID)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(photoIdOrUrl)) {
    // For UUIDs, use the public serve endpoint (no auth required)
    return `${baseUrl}/api/photos/serve/${photoIdOrUrl}`;
  }
  
  // For filenames, prefer direct static access for better performance
  // Check if it's a filename (contains extension but no path)
  if (photoIdOrUrl.includes('.') && !photoIdOrUrl.includes('/')) {
    return `${baseUrl}/api/photos/serve/${photoIdOrUrl}`;
  }
  
  // Default fallback - assume it's a filename and use static access
  return `${baseUrl}/api/photos/serve/${photoIdOrUrl}`;
};

export const getThumbnailUrl = (photoIdOrUrl: string, size: 'small' | 'medium' | 'large' = 'medium'): string => {
  // Get the base photo URL first
  const basePhotoUrl = getPhotoUrl(photoIdOrUrl);
  
  // Add thumbnail size parameter
  const separator = basePhotoUrl.includes('?') ? '&' : '?';
  return `${basePhotoUrl}${separator}size=${size}`;
};

export const getAvatarUrl = (photoIdOrUrl?: string): string | null => {
  if (!photoIdOrUrl) return null;
  return getPhotoUrl(photoIdOrUrl);
};

// Enhanced photo URL function that handles both photo objects and strings
export const getPhotoUrlFromObject = (photo: Photo | string): string => {
  if (typeof photo === 'string') {
    return getPhotoUrl(photo);
  }
  
  // For photo objects, prefer URL, then ID, then filename
  if (photo.url) {
    return getPhotoUrl(photo.url);
  }
  
  if (photo.id) {
    return getPhotoUrl(photo.id);
  }
  
  if (photo.filename) {
    return getPhotoUrl(photo.filename);
  }
  
  return '';
};

export const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Unknown size';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

// Named export functions for backward compatibility
export const deletePhoto = async (id: string): Promise<void> => {
  await photosApi.deletePhoto(id);
};

export const updatePhotoCaption = async (id: string, caption: string): Promise<Photo> => {
  return await photosApi.updatePhoto(id, { caption });
};

export const getPhotosBySpecies = async (speciesId: string): Promise<Photo[]> => {
  return await photosApi.getPhotosBySpecies(speciesId);
};

export const setSpeciesAvatar = async (speciesId: string, photoId: string): Promise<Photo> => {
  return await photosApi.setAsAvatar(photoId, speciesId);
};

export const uploadSpeciesPhoto = async (formData: FormData): Promise<Photo> => {
  const speciesId = formData.get('speciesId');
  formData.delete('speciesId'); // Remove speciesId from FormData since it will be in URL
  // Backend returns Photo directly (not wrapped in success/data)
  const response = await api.post<Photo>(`photos/species/${speciesId}`, formData);
  return response.data;
};

export const uploadBirdPhoto = async (formData: FormData): Promise<Photo> => {
  // Use the specific bird upload endpoint which returns wrapped response
  const response = await api.post<{ success: boolean; data: Photo }>(`photos/bird/${formData.get('birdId')}`, formData);
  return response.data.data;
};

export const deleteSpeciesPhoto = async (photoId: string): Promise<void> => {
  await photosApi.deletePhoto(photoId);
};

export const updateSpeciesPhotoCaption = async (photoId: string, caption: string): Promise<Photo> => {
  return await photosApi.updatePhoto(photoId, { caption });
}; 

